import { Check, ChevronDown, Filter } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { cn } from "~/lib/utils";
import { ProjectListView } from "~/services/projects";

interface ViewMeta {
  /** Short label — on the collapsed trigger and each menu item's title. */
  label: string;
  /** One-line explanation shown muted beneath the label in the open menu. */
  description: string;
}

const VIEW_META: Record<ProjectListView, ViewMeta> = {
  [ProjectListView.USABLE]: {
    label: "Can use",
    description: "Projects you can use (including those you manage)",
  },
  [ProjectListView.OWNED]: {
    label: "Can manage",
    description: "Projects you own",
  },
  [ProjectListView.ALL]: {
    label: "All projects",
    description: "Admin-only list of all projects",
  },
};

interface ProjectViewFilterProps {
  value: ProjectListView;
  /** The views the caller may pick, in menu order. The control hides itself when
   *  fewer than two are offered — a single choice isn't a choice. */
  options: ProjectListView[];
  onChange: (view: ProjectListView) => void;
}

/** Access-scope selector for the projects list: a compact trigger label that
 *  expands into per-option descriptions in the open menu. The caller decides
 *  which options appear; this component is presentational. */
export const ProjectViewFilter = ({ value, options, onChange }: ProjectViewFilterProps) => {
  if (options.length < 2) {
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          aria-label={`View: ${VIEW_META[value].label}`}
        >
          <Filter className="size-4" aria-hidden="true" />
          {VIEW_META[value].label}
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-64">
        <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
          View
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((view) => {
          const meta = VIEW_META[view];
          const isActive = view === value;
          return (
            <DropdownMenuItem
              key={view}
              onSelect={() => onChange(view)}
              className="items-start gap-2 py-2"
            >
              <Check
                className={cn("mt-0.5 size-4 shrink-0", !isActive && "opacity-0")}
                aria-hidden="true"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium leading-none">{meta.label}</span>
                <span className="text-muted-foreground text-xs">{meta.description}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
